<template>
  <iColumn
    :height="chatroomHeight"
    :min-height="roomMinHeight"
    :max-width="getOptionValue(MaxWidth)"
    data-test="event-video-room"
  >
    <iColumn
      width="fill"
      height="fill"
      wrap="nowrap"
      overflow="hidden"
      data-test="event-video-room-surface"
      :theme-mode="roomThemeMode"
      :data-room-theme-mode="roomThemeMode"
      :style-overrides="{
        backgroundColor: roomBackground,
        borderRadius: roomBorderRadius,
      }"
    >
      <template v-if="isWatchOnly && hasResolvedViewerRole">
        <LivestreamWatchView
          :video-track="watchVideoTrack"
          :audio-track="watchAudioTrack"
          :audio-blocked="watchAudioBlocked"
          :is-live="isWatchStreamLive"
          :is-pending="isWatchStreamPending"
          :status-title="watchStatusTitle"
          :status-subtitle="watchStatusSubtitle"
          :viewer-count="watchConnected ? watchAudienceCount : eventPresence.viewers"
          :cover-image="coverImage"
          :countdown-segments="startCountdownSegments"
          :reaction-bursts="watchReactionBursts"
          :can-react="watchConnected"
          :watch-state="watchState"
          :intermission-image="intermissionImage"
          @enable-audio="resumeWatchAudio"
          @send-reaction="sendWatchReaction"
        />
      </template>
      <template v-else-if="!hasJoined">
        <EventJoinCard
          :title="joinCardTitle"
          :subtitle="roomLocked ? 'The host has locked this meeting. You can join once they unlock it.' : joinCardSubtitle"
          :status="roomLocked ? 'Locked' : joinCardState.status"
          :show-join="hasResolvedViewerRole"
          :can-join="canJoinNow && !roomLocked && !joinPending"
          :participant-count="participantCount"
          :is-livestream="isLivestream"
          :join-label="joinLabel"
          :invite-link="inviteLink"
          :event-title="eventName"
          :cover-image="coverImage"
          :when-copy="whenCopy"
          :host-name="hostName"
          :host-logo="hostLogo"
          :people="eventPresence.people || []"
          :countdown-segments="startCountdownSegments"
          @join="handleJoin"
        />
      </template>
      <template v-else>
        <MeetingConversation
          :entity="resolvedEventEntity"
          :event-id="eventIdForModeration"
          :can-moderate-room="canModerateRoom"
          :is-broadcasting="isLivestreamHost"
          @exit-video-room="handleExitVideoRoom"
        />
      </template>
    </iColumn>
  </iColumn>
</template>

<script>
import { callRejoinMarkers } from "%/lib/browser-state-stores";
import { mapActions, mapState } from "pinia";
import { useChannelStore } from "~/stores/channel-store";
import { useVideoCallStore } from "%/apps/video-engine/stores/video-call-store";
import { useVideoCallUiStore } from "%/apps/video-engine/stores/video-call-ui-store";
import { readRejoin, rememberInCall } from "%/apps/video-engine/rejoin-marker";
import { useLivestreamStore } from "%/apps/video-engine/stores/livestream-store";
import { useGeneralStore } from "%/stores/general-store";
import { buildEventInviteLink } from "%/apps/video-engine/event-invite-link";
import { roomMinHeightFor } from "%/apps/video-engine/room-height";
import LivestreamWatchView from "~/components/events/livestream/LivestreamWatchView.vue";
import { EVENT_CONTEXT, EVENT_TYPE_LIVESTREAM } from "%/constants/event-constants";
import App from "@bloglovin/inspire-engine/App";
import { JOIN_WAIT, decideRoomJoin, readJoinIntent, stripJoinIntent } from "%/apps/video-engine/event-room-join";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import MinHeight from "%/dynamic-components/render-engine/options/spacing-options/min-height";
import MaxWidth from "%/dynamic-components/render-engine/options/spacing-options/max-width";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import BorderRadius from "%/dynamic-components/render-engine/options/border-options/border-radius";
import PositioningOptions from "%/dynamic-components/render-engine/bundles/positioning-options";
import AppearanceOptions from "%/dynamic-components/render-engine/bundles/appearance-options";
import LayoutWidthHeightOptions from "%/dynamic-components/render-engine/bundles/layout-width-height-options";
import SpacingOptions from "%/dynamic-components/render-engine/bundles/spacing-options";
import BackgroundOptions from "%/dynamic-components/render-engine/bundles/background-options";
import EventRoomOptions from "%/dynamic-components/render-engine/bundles/event-room-options";
import RoomTheme, { ROOM_THEME_MODES } from "%/dynamic-components/render-engine/options/room-theme";
import ShowEventTitle from "%/dynamic-components/render-engine/options/show-event-title";
import ShowElapsedTimer from "%/dynamic-components/render-engine/options/show-elapsed-timer";
import ShowLiveBadge from "%/dynamic-components/render-engine/options/show-live-badge";
import ShowViewerCount from "%/dynamic-components/render-engine/options/show-viewer-count";
import ShowStatusChip from "%/dynamic-components/render-engine/options/show-status-chip";
import IntermissionImage from "%/dynamic-components/render-engine/options/intermission-image";
import IntermissionText from "%/dynamic-components/render-engine/options/intermission-text";
import { WATCH_STATES, resolveWatchState, describeWatchState } from "%/apps/video-engine/watch-state";
import { ROOM_APPEARANCE_KEY, readRoomToggle } from "%/apps/video-engine/room-appearance";
import { computed, defineAsyncComponent } from "vue";
import EventJoinCard from "~/components/events/EventJoinCard.vue";
import { describeJoinCard } from "%/dynamic-component-editor/helpers/event-join-card";

const MeetingConversation = defineAsyncComponent(
  () => import("~/dynamic-components/standard-pages/event-page/MeetingConversation.vue"),
);
import { EventCountdownElementMixin } from "~/dynamic-components/standard-components/event-card/event-countdown-element-mixin";
import { countdownSegments } from "%/dynamic-component-editor/helpers/event-countdown";
import { ROOM_CONTEXT_KEY } from "%/apps/video-engine/room-context";
import dayjs from "dayjs";

const CLOCK_TICK_MS = 15000;
const ROOM_MIN_HEIGHT = 300;
const COMPACT_ROOM_WIDTH = 760;
const ROOM_SURFACE = "paper";
const ROOM_RADIUS = "large";
const UNSET_SURFACE_VALUES = ["transparent", "none", "unset", null, undefined];
const UNSET_RADIUS_VALUES = ["0px", 0, "none", "unset", null, undefined];

export default {
  name: "EventVideoRoom",
  components: { EventJoinCard, MeetingConversation, LivestreamWatchView },
  mixins: [EventCountdownElementMixin],
  elementPanelSettings: {
    displayName: "Event Video Room",
    displayIcon: "video",
  },
  provide() {
    return {
      [ROOM_APPEARANCE_KEY]: computed(() => this.roomAppearance),
      [ROOM_CONTEXT_KEY]: computed(() => this.roomContext),
    };
  },
  data() {
    return {
      chatroomHeight: "fill",
      hasJoined: false,
      joinPending: false,
      wantsAutoRejoin: false,
      wantsJoinFromLink: false,
      surfaceWidth: 0,
      viewportHeight: 0,
      hasStartedWatching: false,
      canModerateRoom: false,
      hasResolvedPresence: false,
      nowSeconds: Math.floor(Date.now() / 1000),
      clockTimer: null,
      MinHeight,
      MaxWidth,
      BackgroundColor,
      BorderRadius,
    };
  },
  optionBundles: [
    PositioningOptions,
    new AppearanceOptions({ "default.border-radius": "large" }, { "border-radius": false }),
    new LayoutWidthHeightOptions({ "default.min-height": ROOM_MIN_HEIGHT }, {
      "max-width": true,
      "max-height": true,
      "min-width": true,
    }),
    new SpacingOptions(),
    new BackgroundOptions({
      "default.background-color": "none",
    }, { "background-gradient": true }),
    new EventRoomOptions(),
  ],
  computed: {
    roomThemeMode() {
      return ROOM_THEME_MODES[this.getOptionValue(RoomTheme)] || ROOM_THEME_MODES.match;
    },
    roomAppearance() {
      return {
        themeMode: ROOM_THEME_MODES.match,
        hostPaintsSurface: true,
        showEventTitle: readRoomToggle(this, ShowEventTitle),
        showElapsedTimer: readRoomToggle(this, ShowElapsedTimer),
        showLiveBadge: readRoomToggle(this, ShowLiveBadge),
        showViewerCount: readRoomToggle(this, ShowViewerCount),
        showStatusChip: readRoomToggle(this, ShowStatusChip),
      };
    },
    roomBackground() {
      const configured = this.getOptionValue(BackgroundColor);

      return UNSET_SURFACE_VALUES.includes(configured) ? ROOM_SURFACE : configured;
    },
    roomBorderRadius() {
      const configured = this.getOptionValue(BorderRadius);

      return UNSET_RADIUS_VALUES.includes(configured) ? ROOM_RADIUS : configured;
    },
    resolvedEventEntity() {
      const eventId = this.eventIdFromUrl;
      if (!eventId) {
        return {};
      }
      const app = App.get(contextualAppName());

      return app?.getEntity("Event", { id: eventId }) || {};
    },
    eventIdFromUrl() {
      const [, id] = window.location.pathname.match(/\/event\/(\d+)/) || [];

      return Number(id || 0);
    },
    isLivestream() {
      return this.resolvedEventEntity?.eventType === EVENT_TYPE_LIVESTREAM;
    },
    isWatchOnly() {
      return this.isLivestream && !this.canModerateRoom;
    },
    isLivestreamHost() {
      return this.isLivestream && this.canModerateRoom;
    },
    hasResolvedViewerRole() {
      return this.hasResolvedPresence || !this.isSignedIn;
    },
    ...mapState(useVideoCallStore, ["presenceByEventId", "roomStateByEventId"]),
    ...mapState(useVideoCallUiStore, ["callPhase"]),
    inviteLink() {
      return buildEventInviteLink(this.webUrl, this.eventIdFromUrl);
    },
    roomMinHeight() {
      if (!this.hasJoined) {
        return this.getOptionValue(MinHeight);
      }

      return roomMinHeightFor({
        optionMinHeight: Number(this.getOptionValue(MinHeight)) || 0,
        surfaceWidth: this.surfaceWidth,
        viewportHeight: this.viewportHeight,
        hasJoined: true,
      });
    },
    roomLocked() {
      return Boolean(this.roomStateByEventId[this.eventIdForModeration]?.locked) && !this.canModerateRoom;
    },
    ...mapState(useGeneralStore, { isSignedIn: "isLoggedIn", webUrl: "webUrl" }),
    ...mapState(useLivestreamStore, {
      watchVideoTrack: "remoteVideoTrack",
      watchConnected: "connected",
      watchConnecting: "connecting",
      watchReconnecting: "reconnecting",
      watchError: "error",
      watchAudienceCount: "audienceCount",
      watchReactionBursts: "reactionBursts",
      watchBroadcastStatusFromRoom: "broadcastStatus",
      watchAudioTrack: "remoteAudioTrack",
      watchAudioBlocked: "audioPlaybackBlocked",
    }),
    watchBroadcastStatus() {
      if (this.watchConnected) {
        return this.watchBroadcastStatusFromRoom;
      }

      return this.eventPresence.broadcastStatus || "idle";
    },
    watchState() {
      return resolveWatchState({
        signedIn: this.isSignedIn,
        error: this.watchError,
        connecting: this.watchConnecting,
        reconnecting: this.watchReconnecting,
        hasVideo: Boolean(this.watchVideoTrack),
        broadcastStatus: this.watchBroadcastStatus,
        ended: this.joinCardState.phase === "ended",
      });
    },
    watchCopy() {
      return describeWatchState(this.watchState, { intermissionText: this.intermissionText });
    },
    intermissionImage() {
      return String(this.getOptionValue(IntermissionImage) || "");
    },
    intermissionText() {
      return String(this.getOptionValue(IntermissionText) || "");
    },
    isWatchStreamLive() {
      return this.watchState === WATCH_STATES.LIVE;
    },
    isWatchStreamPending() {
      return this.watchState === WATCH_STATES.CONNECTING || this.watchState === WATCH_STATES.RECONNECTING;
    },
    watchStatusTitle() {
      return this.watchCopy.title;
    },
    watchStatusSubtitle() {
      return this.watchCopy.subtitle;
    },
    eventIdForModeration() {
      return this.eventIdFromUrl;
    },
    pinnedOccurrenceStart() {
      if (typeof window === "undefined") {
        return 0;
      }

      return Number(new URLSearchParams(window.location.search).get("occurrence")) || 0;
    },
    joinCardState() {
      return describeJoinCard({
        entity: this.resolvedEventEntity,
        now: this.nowSeconds,
        canModerate: this.canModerateRoom,
        isWatchOnly: this.isWatchOnly,
        isLivestreamHost: this.isLivestreamHost,
        isLivestream: this.isLivestream,
        presence: this.eventPresence,
      });
    },
    autoRejoinReady() {
      return (this.wantsAutoRejoin || this.wantsJoinFromLink)
        && !this.isWatchOnly
        && !this.hasJoined
        && this.eventConversationId > 0
        && this.canJoinNow;
    },
    eventEntity() {
      const app = App.get(contextualAppName());

      return app ? app.getEntity("Event", { id: this.eventIdFromUrl }) : null;
    },
    eventConversationId() {
      return Number(this.eventEntity?.conversationId || 0);
    },
    eventHasLoaded() {
      return this.eventEntity?.isHydrated === true;
    },
    joinLabel() {
      if (this.joinPending) {
        return "Joining…";
      }

      return this.isLivestreamHost ? "Go live" : "Join";
    },
    canJoinNow() {
      return this.joinCardState.canJoin;
    },
    joinCardTitle() {
      return this.joinCardState.title;
    },
    joinCardSubtitle() {
      return this.joinCardState.subtitle;
    },
    eventPresence() {
      return this.presenceByEventId[this.eventIdFromUrl] || { participants: 0, publishers: 0, viewers: 0, people: [], broadcastStatus: "idle" };
    },
    eventName() {
      return this.resolvedEventEntity?.title || this.entity?.title || "";
    },
    coverImage() {
      return this.resolvedEventEntity?.image || this.entity?.imageUrl || "";
    },
    eventStartSeconds() {
      return Number(this.resolvedEventEntity?.startTime || this.entity?.startTimestamp || 0);
    },
    eventEndSeconds() {
      return Number(this.resolvedEventEntity?.endTime || this.entity?.endTimestamp || 0);
    },
    whenCopy() {
      if (!this.eventStartSeconds) {
        return "";
      }
      const start = dayjs.unix(this.eventStartSeconds).format("ddd, MMM D · h:mm A");

      return this.eventEndSeconds ? `${start} – ${dayjs.unix(this.eventEndSeconds).format("h:mm A")}` : start;
    },
    hostName() {
      return this.eventPresence.host?.name || "";
    },
    hostLogo() {
      return this.eventPresence.host?.logoUrl || "";
    },
    startCountdownSegments() {
      const remaining = this.eventStartSeconds - this.countdownNow;

      return this.eventStartSeconds && remaining > 0 ? countdownSegments(remaining) : null;
    },
    roomContext() {
      return {
        title: this.eventName,
        coverImage: this.coverImage,
        whenCopy: this.whenCopy,
        hostName: this.hostName,
        hostLogo: this.hostLogo,
        people: this.eventPresence.people || [],
        isLivestream: this.isLivestream,
        compact: this.surfaceWidth > 0 && this.surfaceWidth < COMPACT_ROOM_WIDTH,
      };
    },
    participantCount() {
      return this.eventPresence.participants;
    },
    shouldPollPresence() {
      return this.isSignedIn && this.callPhase !== "in-call" && !this.watchConnected;
    },
  },
  watch: {
    autoRejoinReady: {
      immediate: true,
      handler(ready) {
        if (!ready) {
          return;
        }
        if (this.wantsJoinFromLink) {
          rememberInCall(callRejoinMarkers, this.eventIdFromUrl);
        }
        this.wantsAutoRejoin = false;
        this.wantsJoinFromLink = false;
        this.handleJoin();
      },
    },
    eventConversationId(conversationId) {
      if (conversationId > 0 && this.joinPending) {
        this.handleJoin();
      }
    },
  },
  mounted() {
    this.measureRoom();
    window.addEventListener("resize", this.measureRoom);
    if (typeof ResizeObserver !== "undefined") {
      this.roomResizeObserver = new ResizeObserver(this.measureRoom);
      this.roomResizeObserver.observe(this.$el);
    }
    this.clockTimer = window.setInterval(() => {
      this.nowSeconds = Math.floor(Date.now() / 1000);
      if (this.shouldPollPresence) {
        this.refreshPresence();
      }
    }, CLOCK_TICK_MS);
    this.refreshPresence();
    callRejoinMarkers.ready().then(() => {
      this.wantsAutoRejoin = Boolean(readRejoin(callRejoinMarkers, this.eventIdFromUrl));
    });
    if (this.isSignedIn && readJoinIntent(window.location.search)) {
      this.wantsJoinFromLink = true;
      window.history.replaceState(window.history.state, "", stripJoinIntent(window.location.href));
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.measureRoom);
    if (this.roomResizeObserver) {
      this.roomResizeObserver.disconnect();
    }
    window.clearInterval(this.clockTimer);
    if (this.hasStartedWatching) {
      this.disconnectFromStream();
    }
  },
  methods: {
    measureRoom() {
      this.surfaceWidth = this.$el?.getBoundingClientRect ? this.$el.getBoundingClientRect().width : 0;
      this.viewportHeight = window.innerHeight;
    },
    refreshPresence() {
      const eventId = this.eventIdForModeration;
      if (!eventId || !this.isSignedIn) {
        return;
      }

      this.fetchEventRoomState(eventId, () => {});
      this.fetchEventPresence(eventId, presence => {
        if (!presence) {
          return;
        }
        this.nowSeconds = Math.floor(Date.now() / 1000);
        this.hasResolvedPresence = true;
        this.canModerateRoom = presence.canModerate;
        if (this.isWatchOnly) {
          this.startWatching();
        }
      }, this.pinnedOccurrenceStart);
    },
    startWatching() {
      if (this.hasStartedWatching) {
        return;
      }
      this.hasStartedWatching = true;
      this.connectToStream(this.eventIdFromUrl, "viewer");
    },
    ...mapActions(useChannelStore, ["switchToEntity"]),
    ...mapActions(useVideoCallStore, ["fetchEventPresence", "fetchEventRoomState"]),
    ...mapActions(useLivestreamStore, {
      connectToStream: "connect",
      sendWatchReaction: "sendReaction",
      disconnectFromStream: "disconnect",
      resumeWatchAudio: "resumeAudioPlayback",
    }),
    handleJoin() {
      const decision = decideRoomJoin({
        eventId: this.eventIdFromUrl,
        eventHasLoaded: this.eventHasLoaded,
        conversationId: this.eventConversationId,
      });
      if (decision === JOIN_WAIT) {
        this.joinPending = true;

        return;
      }
      this.joinPending = false;
      this.switchToEntity(this.eventIdFromUrl, EVENT_CONTEXT, this.eventConversationId, false);
      this.hasJoined = true;
      this.refreshPresence();
    },
    handleExitVideoRoom() {
      this.hasJoined = false;
      this.refreshPresence();
    },
  },
};
</script>

